import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PublishIcon from '@mui/icons-material/Publish';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import {
    DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    deleteCarouselImage, deleteScoreReportImage, deleteYouTubeContent, getCarouselImages, getQuickReadAloudPrompts,
    getScoreReportImages, getYouTubeContents, reorderCarouselImages, reorderScoreReportImages, saveQuickReadAloudPrompt,
    saveYouTubeContent, uploadCarouselImage, uploadScoreReportImage, publishHomePageContentToTarget,
    validateHomePageContentPublish, type HomePageContentPromotionResult, type HomePageImage,
    type QuickReadAloudPromptInput, type YouTubeContent, type YouTubeContentInput,
} from './homePageContentsApi';
import { getAdminEnvironmentKey } from '../admin/adminEnvironment';

const emptyInput: YouTubeContentInput = { title: '', youtubeUrl: '', displayOrder: 0, isActive: true };

function getYouTubeId(rawUrl: string): string | null {
    try {
        const url = new URL(rawUrl.trim());
        const host = url.hostname.replace(/^www\./, '').toLowerCase();
        if (host === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0] ?? null;
        if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
            if (url.pathname === '/watch') return url.searchParams.get('v');
            const parts = url.pathname.split('/').filter(Boolean);
            if (['embed', 'shorts', 'live'].includes(parts[0] ?? '')) return parts[1] ?? null;
        }
    } catch { return null; }
    return null;
}

function VideoPreview({ url, title }: { url: string; title: string }) {
    const videoId = getYouTubeId(url);
    if (!videoId) return <Alert severity="warning">Enter a valid YouTube watch, short, live, youtu.be, or embed link to see the preview.</Alert>;
    return (
        <Box component="iframe" title={title || 'YouTube preview'} src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen
            sx={{ width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: 2, bgcolor: '#0f172a' }} />
    );
}

function QuickReadAloudEditor() {
    const notify = useNotify();
    const [promptId, setPromptId] = useState<string>();
    const [form, setForm] = useState<QuickReadAloudPromptInput>({ paragraph: '', examName: 'Academic', isActive: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true); setError('');
        try {
            const prompts = await getQuickReadAloudPrompts();
            const selected = prompts.find((item) => item.isActive) ?? prompts[0];
            if (selected) {
                setPromptId(selected.promptId);
                setForm({ paragraph: selected.paragraph, examName: selected.examName, isActive: selected.isActive });
            }
        } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to load Quick Read Aloud prompt'); }
        finally { setLoading(false); }
    };
    useEffect(() => { void load(); }, []);

    const save = async () => {
        const paragraph = form.paragraph.trim();
        if (paragraph.length < 20 || paragraph.length > 2000) {
            notify('Paragraph must be between 20 and 2,000 characters', { type: 'warning' }); return;
        }
        setSaving(true);
        try {
            const saved = await saveQuickReadAloudPrompt({ ...form, paragraph }, promptId);
            setPromptId(saved.promptId); setForm({ paragraph: saved.paragraph, examName: saved.examName, isActive: saved.isActive });
            notify('Quick Read Aloud prompt updated', { type: 'success' });
        } catch (reason) { notify(reason instanceof Error ? reason.message : 'Save failed', { type: 'error' }); }
        finally { setSaving(false); }
    };

    if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" action={<Button onClick={() => void load()}>Retry</Button>}>{error}</Alert>;
    return (
        <Stack gap={2.5} maxWidth={900}>
            <Box><Typography variant="h6" fontWeight={700}>Marketing Quick Read Aloud prompt</Typography><Typography color="text.secondary">Anonymous visitors will read this paragraph before submitting their recording.</Typography></Box>
            <TextField multiline minRows={7} required label="Read Aloud paragraph" value={form.paragraph}
                onChange={(event) => setForm({ ...form, paragraph: event.target.value })}
                helperText={`${form.paragraph.trim().length}/2000 characters`} />
            <TextField select label="PTE exam scoring" value={form.examName} onChange={(event) => setForm({ ...form, examName: event.target.value as 'Academic' | 'Core' })} sx={{ maxWidth: 280 }}>
                <MenuItem value="Academic">Academic</MenuItem><MenuItem value="Core">Core</MenuItem>
            </TextField>
            <FormControlLabel control={<Checkbox checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />} label="Active on public website" />
            <Box><Button variant="contained" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save Quick Read Aloud Prompt'}</Button></Box>
            <Alert severity="info">Public content endpoint: <strong>/api/v1/HomePageContents/quick-read-aloud</strong></Alert>
        </Stack>
    );
}

type ImageCollectionEditorProps = {
    title: string;
    description: string;
    singularLabel: string;
    emptyTitle: string;
    endpoint: string;
    getImages: () => Promise<HomePageImage[]>;
    uploadImage: (file: File, id?: string) => Promise<HomePageImage>;
    deleteImage: (id: string) => Promise<void>;
    reorderImages: (imageIds: string[]) => Promise<HomePageImage[]>;
};

type SortableImageCardProps = {
    image: HomePageImage;
    index: number;
    singularLabel: string;
    disabled: boolean;
    onReplace: (image: HomePageImage, file: File | undefined) => void;
    onRemove: (image: HomePageImage) => void;
};

function ImageCardContent({ image, index, singularLabel, disabled, dragHandle, onReplace, onRemove }: SortableImageCardProps & { dragHandle?: React.ReactNode }) {
    return (
        <>
            <Box component="img" src={image.imageUrl} alt={singularLabel} sx={{ width: '100%', height: 260, objectFit: 'contain', bgcolor: '#f8fafc', display: 'block', pointerEvents: 'none' }} />
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                        {dragHandle}
                        <Box minWidth={0}>
                            <Typography variant="caption" fontWeight={800} color="primary.main">Position {index + 1}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap title={image.fileName}>{image.fileName}</Typography>
                        </Box>
                    </Stack>
                    <Box flexShrink={0}>
                        <IconButton component="label" aria-label={`Replace ${singularLabel} at position ${index + 1}`} disabled={disabled} onPointerDown={(event) => event.stopPropagation()}><EditOutlinedIcon /><input hidden type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => { onReplace(image, event.target.files?.[0]); event.target.value = ''; }} /></IconButton>
                        <IconButton aria-label={`Delete ${singularLabel} at position ${index + 1}`} color="error" disabled={disabled} onPointerDown={(event) => event.stopPropagation()} onClick={() => onRemove(image)}><DeleteOutlineIcon /></IconButton>
                    </Box>
                </Stack>
            </CardContent>
        </>
    );
}

function SortableImageCard(props: SortableImageCardProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.image.id, disabled: props.disabled });
    return (
        <Card ref={setNodeRef} variant="outlined" {...attributes} {...listeners} sx={{
            position: 'relative', transform: CSS.Transform.toString(transform), transition,
            opacity: isDragging ? 0.28 : 1, borderColor: isDragging ? 'primary.main' : 'divider',
            boxShadow: isDragging ? 4 : 0, zIndex: isDragging ? 1 : 'auto', touchAction: 'none',
            cursor: props.disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        }}>
            <ImageCardContent {...props} dragHandle={
                <IconButton aria-label={`Drag ${props.singularLabel} at position ${props.index + 1} to reorder`}
                    disabled={props.disabled} size="small" sx={{ cursor: props.disabled ? 'default' : 'grab', touchAction: 'none', bgcolor: 'action.hover', '&:active': { cursor: 'grabbing' } }}>
                    <DragIndicatorOutlinedIcon />
                </IconButton>
            } />
        </Card>
    );
}

function ImageCollectionEditor({ title, description, singularLabel, emptyTitle, endpoint, getImages, uploadImage, deleteImage, reorderImages }: ImageCollectionEditorProps) {
    const notify = useNotify();
    const [images, setImages] = useState<HomePageImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setImages(await getImages()); }
        catch (reason) { setError(reason instanceof Error ? reason.message : `Unable to load ${title.toLowerCase()}`); }
        finally { setLoading(false); }
    }, [getImages, title]);
    useEffect(() => { void load(); }, [load]);

    const upload = async (files: FileList | null) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            for (const file of Array.from(files)) await uploadImage(file);
            notify(`${files.length} ${singularLabel}${files.length === 1 ? '' : 's'} uploaded`, { type: 'success' });
            await load();
        } catch (reason) { notify(reason instanceof Error ? reason.message : 'Upload failed', { type: 'error' }); }
        finally { setUploading(false); }
    };

    const replace = async (image: HomePageImage, file: File | undefined) => {
        if (!file) return;
        setUploading(true);
        try { await uploadImage(file, image.id); notify(`${singularLabel} replaced`, { type: 'success' }); await load(); }
        catch (reason) { notify(reason instanceof Error ? reason.message : 'Replace failed', { type: 'error' }); }
        finally { setUploading(false); }
    };

    const remove = async (image: HomePageImage) => {
        if (!window.confirm(`Delete this ${singularLabel}?`)) return;
        try { await deleteImage(image.id); setImages((current) => current.filter((item) => item.id !== image.id)); notify(`${singularLabel} deleted`, { type: 'success' }); }
        catch (reason) { notify(reason instanceof Error ? reason.message : 'Delete failed', { type: 'error' }); }
    };

    const finishReorder = async ({ active, over }: DragEndEvent) => {
        setActiveId(null);
        if (!over || active.id === over.id || reordering) return;
        const from = images.findIndex((image) => image.id === active.id);
        const to = images.findIndex((image) => image.id === over.id);
        if (from < 0 || to < 0) return;
        const previous = images;
        const next = arrayMove(images, from, to);
        setImages(next.map((image, index) => ({ ...image, order: index + 1 })));
        setReordering(true);
        try {
            setImages(await reorderImages(next.map((image) => image.id)));
            notify('Image order updated', { type: 'success' });
        } catch (reason) {
            setImages(previous);
            notify(reason instanceof Error ? reason.message : 'Unable to update image order', { type: 'error' });
        } finally { setReordering(false); }
    };

    if (loading) return <Box textAlign="center" py={8}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" action={<Button onClick={() => void load()}>Retry</Button>}>{error}</Alert>;
    return (
        <Stack gap={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={2}>
                <Box><Typography variant="h6" fontWeight={700}>{title}</Typography><Typography color="text.secondary">{description} Drag any part of a card into its new position.</Typography></Box>
                <Button component="label" variant="contained" startIcon={<UploadFileIcon />} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Upload Images'}
                    <input hidden multiple type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => { void upload(event.target.files); event.target.value = ''; }} />
                </Button>
            </Stack>
            {images.length === 0 ? <Box textAlign="center" py={7}><ImageOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled' }} /><Typography variant="h6">{emptyTitle}</Typography><Typography color="text.secondary">Upload the first image to expose it through the public endpoint.</Typography></Box> : null}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={({ active }) => setActiveId(String(active.id))} onDragCancel={() => setActiveId(null)} onDragEnd={(event) => void finishReorder(event)}>
                <SortableContext items={images.map((image) => image.id)} strategy={rectSortingStrategy}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                        {images.map((image, index) => <SortableImageCard key={image.id} image={image} index={index} singularLabel={singularLabel} disabled={uploading || reordering} onReplace={(item, file) => void replace(item, file)} onRemove={(item) => void remove(item)} />)}
                    </Box>
                </SortableContext>
                <DragOverlay dropAnimation={{ duration: 180, easing: 'ease-out' }}>
                    {activeId ? (() => { const index = images.findIndex((image) => image.id === activeId); const image = images[index]; return image ? <Card variant="outlined" sx={{ boxShadow: 12, borderColor: 'primary.main', cursor: 'grabbing', transform: 'rotate(1deg)' }}><ImageCardContent image={image} index={index} singularLabel={singularLabel} disabled onReplace={() => undefined} onRemove={() => undefined} /></Card> : null; })() : null}
                </DragOverlay>
            </DndContext>
            {reordering ? <Alert severity="info" icon={<CircularProgress size={18} />}>Saving the new image order…</Alert> : null}
            <Alert severity="info">Public content endpoint: <strong>{endpoint}</strong></Alert>
        </Stack>
    );
}

function ScoreReportsEditor() {
    return <ImageCollectionEditor title="Homepage score reports" description="Upload images shown as score report examples on the public homepage." singularLabel="score report image" emptyTitle="No score report images yet" endpoint="/api/v1/HomePageContents/score-reports" getImages={getScoreReportImages} uploadImage={uploadScoreReportImage} deleteImage={deleteScoreReportImage} reorderImages={reorderScoreReportImages} />;
}

function CarouselImagesEditor() {
    return <ImageCollectionEditor title="Homepage carousel images" description="Upload images displayed in the public homepage carousel." singularLabel="carousel image" emptyTitle="No carousel images yet" endpoint="/api/v1/HomePageContents/carousel-images" getImages={getCarouselImages} uploadImage={uploadCarouselImage} deleteImage={deleteCarouselImage} reorderImages={reorderCarouselImages} />;
}

export function HomePageContentsPage() {
    const notify = useNotify();
    const [items, setItems] = useState<YouTubeContent[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<YouTubeContent | null>(null);
    const [form, setForm] = useState<YouTubeContentInput>(emptyInput);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishPreview, setPublishPreview] = useState<HomePageContentPromotionResult | null>(null);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [validatingPublish, setValidatingPublish] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [tab, setTab] = useState<'youtube' | 'quick-read-aloud' | 'score-reports' | 'carousel-images'>('youtube');
    const environment = getAdminEnvironmentKey();
    const publishTarget = environment === 'dev' ? 'Stage' : environment === 'stage' ? 'Production' : null;

    const sortedItems = useMemo(() => [...items].sort((a, b) => a.displayOrder - b.displayOrder || a.createdAt.localeCompare(b.createdAt)), [items]);
    const load = async () => {
        setLoading(true); setError('');
        try { setItems(await getYouTubeContents()); } catch (reason) { setError(reason instanceof Error ? reason.message : 'Unable to load YouTube content'); }
        finally { setLoading(false); }
    };
    useEffect(() => { void load(); }, []);

    const openCreate = () => { setEditing(null); setForm(emptyInput); setDialogOpen(true); };
    const openEdit = (item: YouTubeContent) => { setEditing(item); setForm({ title: item.title, youtubeUrl: item.youtubeUrl, displayOrder: item.displayOrder, isActive: item.isActive }); setDialogOpen(true); };
    const submit = async () => {
        if (!getYouTubeId(form.youtubeUrl)) { notify('Please enter a valid YouTube URL', { type: 'warning' }); return; }
        setSaving(true);
        try { await saveYouTubeContent({ ...form, title: form.title.trim(), youtubeUrl: form.youtubeUrl.trim() }, editing?.id); setDialogOpen(false); notify(editing ? 'YouTube content updated' : 'YouTube content added', { type: 'success' }); await load(); }
        catch (reason) { notify(reason instanceof Error ? reason.message : 'Save failed', { type: 'error' }); }
        finally { setSaving(false); }
    };
    const remove = async (item: YouTubeContent) => {
        if (!window.confirm(`Delete “${item.title || item.youtubeUrl}”?`)) return;
        try { await deleteYouTubeContent(item.id); setItems((current) => current.filter((entry) => entry.id !== item.id)); notify('YouTube content deleted', { type: 'success' }); }
        catch (reason) { notify(reason instanceof Error ? reason.message : 'Delete failed', { type: 'error' }); }
    };

    const openPublishPreview = async () => {
        setValidatingPublish(true);
        try {
            setPublishPreview(await validateHomePageContentPublish());
            setPublishDialogOpen(true);
        } catch (reason) { notify(reason instanceof Error ? reason.message : 'Unable to validate homepage publish', { type: 'error' }); }
        finally { setValidatingPublish(false); }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            const result = await publishHomePageContentToTarget();
            setPublishPreview(result);
            if (!result.published) {
                notify(result.blockers[0] ?? 'Homepage content was not published', { type: 'error' });
                return;
            }
            notify(`Homepage content published to ${publishTarget}`, { type: 'success' });
            setPublishDialogOpen(false);
        } catch (reason) { notify(reason instanceof Error ? reason.message : 'Unable to publish homepage content', { type: 'error' }); }
        finally { setPublishing(false); }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1440, mx: 'auto' }}>
            <Title title="Home Page Contents" />
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2.5}>
                <Box><Typography variant="h4" fontWeight={800}>Home Page Contents</Typography><Typography color="text.secondary">Manage content exposed to the public website.</Typography></Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                    {publishTarget ? <Button variant="contained" color="success" startIcon={<PublishIcon />} onClick={() => void openPublishPreview()} disabled={validatingPublish || publishing}>{validatingPublish ? 'Checking…' : `Publish to ${publishTarget}`}</Button> : null}
                    {tab === 'youtube' ? <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add YouTube Link</Button> : null}
                </Stack>
            </Stack>
            <Card variant="outlined">
                <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}><Tab value="youtube" icon={<YouTubeIcon />} iconPosition="start" label="YouTube Content" /><Tab value="quick-read-aloud" icon={<RecordVoiceOverIcon />} iconPosition="start" label="Quick Read Aloud" /><Tab value="score-reports" icon={<ImageOutlinedIcon />} iconPosition="start" label="Score Reports" /><Tab value="carousel-images" icon={<ImageOutlinedIcon />} iconPosition="start" label="Carousel Images" /></Tabs>
                <CardContent>
                    {tab === 'youtube' ? <>
                    {loading ? <Box textAlign="center" py={8}><CircularProgress /></Box> : null}
                    {!loading && error ? <Alert severity="error" action={<Button onClick={() => void load()}>Retry</Button>}>{error}</Alert> : null}
                    {!loading && !error && sortedItems.length === 0 ? <Box textAlign="center" py={8}><YouTubeIcon sx={{ fontSize: 56, color: 'text.disabled' }} /><Typography variant="h6">No YouTube content yet</Typography><Typography color="text.secondary" mb={2}>Add your first link and preview it here.</Typography><Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Add YouTube Link</Button></Box> : null}
                    {!loading && !error ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>{sortedItems.map((item) => <Card key={item.id} variant="outlined"><VideoPreview url={item.youtubeUrl} title={item.title} /><CardContent><Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}><Box minWidth={0}><Typography fontWeight={700}>{item.title || 'Untitled video'}</Typography><Typography variant="body2" color="text.secondary" noWrap title={item.youtubeUrl}>{item.youtubeUrl}</Typography><Typography variant="caption" color={item.isActive ? 'success.main' : 'text.disabled'}>{item.isActive ? 'Active' : 'Inactive'} · Order {item.displayOrder}</Typography></Box><Box flexShrink={0}><IconButton aria-label="Edit" onClick={() => openEdit(item)}><EditOutlinedIcon /></IconButton><IconButton aria-label="Delete" color="error" onClick={() => void remove(item)}><DeleteOutlineIcon /></IconButton></Box></Stack></CardContent></Card>)}</Box> : null}
                    </> : tab === 'quick-read-aloud' ? <QuickReadAloudEditor /> : tab === 'score-reports' ? <ScoreReportsEditor /> : <CarouselImagesEditor />}
                </CardContent>
            </Card>
            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editing ? 'Edit YouTube Content' : 'Add YouTube Content'}</DialogTitle>
                <DialogContent><Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(300px, 1fr)' }, gap: 2.5, pt: 1 }}><Stack gap={2}><TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /><TextField required label="YouTube URL" value={form.youtubeUrl} onChange={(e) => setForm({ ...form, youtubeUrl: e.target.value })} helperText="Supports youtube.com, youtu.be, Shorts, Live, and embed URLs." /><TextField type="number" label="Display order" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) || 0 })} /><FormControlLabel control={<Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />} label="Active on public endpoint" /></Stack><Box><Typography variant="subtitle2" mb={1}>Preview</Typography><VideoPreview url={form.youtubeUrl} title={form.title} /></Box></Box></DialogContent>
                <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={() => void submit()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button></DialogActions>
            </Dialog>
            <Dialog open={publishDialogOpen} onClose={() => !publishing && setPublishDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Publish homepage content to {publishTarget}</DialogTitle>
                <DialogContent>
                    {publishPreview ? <Stack gap={2} pt={1}>
                        <Typography color="text.secondary">This promotes all Home Page Contents from {publishPreview.sourceEnvironment.toUpperCase()} to {publishPreview.targetEnvironment.toUpperCase()}: YouTube links, Quick Read Aloud prompts, score reports, and carousel images.</Typography>
                        {publishPreview.blockers.map((message) => <Alert key={message} severity="error">{message}</Alert>)}
                        {publishPreview.warnings.map((message) => <Alert key={message} severity="warning">{message}</Alert>)}
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                            {Object.entries(publishPreview.changes).map(([name, counts]) => <Paper key={name} variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                                <Typography fontWeight={700}>{name === 'quickReadAloud' ? 'Quick Read Aloud' : 'YouTube'}</Typography>
                                <Typography variant="caption" color="text.secondary">Add {counts.insert} · Update {counts.update} · Deactivate {counts.deactivate} · Remove {counts.remove}</Typography>
                            </Paper>)}
                        </Stack>
                        <Paper variant="outlined" sx={{ p: 1.5 }}><Typography fontWeight={700}>Homepage images</Typography><Typography variant="body2" color="text.secondary">Copy {publishPreview.imageFilesToCopy} · Remove {publishPreview.imageFilesToRemove} target-only file(s)</Typography></Paper>
                        {publishPreview.canPublish ? <Alert severity="success">Validation passed. Homepage content is ready to publish.</Alert> : null}
                    </Stack> : null}
                </DialogContent>
                <DialogActions><Button onClick={() => setPublishDialogOpen(false)} disabled={publishing}>Cancel</Button><Button variant="contained" color="success" startIcon={<PublishIcon />} onClick={() => void publish()} disabled={publishing || !publishPreview?.canPublish}>{publishing ? 'Publishing…' : `Confirm publish to ${publishTarget}`}</Button></DialogActions>
            </Dialog>
        </Box>
    );
}
